import React, { useState } from 'react';
import { get, sortBy, first, isEmpty, last } from 'lodash';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { DateTime } from 'luxon';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Grid,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import {
  DataDriveRunTypes, RunScheduleStatusNames, isOnMobile, sortedRunTypes,
} from '../../../lib/utils';
import RadioSelect from './radio-select';
import { EditRunSchedule } from './edit-run-schedule';
import { PendingRunModal } from './pending-run-modal';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);

const styles = {
  flexRow: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  contentStyle: {
    paddingBottom: 5,
  },
  content: {
    maxHeight: '480px',
    marginRight: '12px',
    paddingRight: '32px',
    maxWidth: '360px',
    overflowY: 'auto',
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  error: {
    width: '300px',
    marginTop: '20px',
    float: 'right',
    marginRight: '20px',
  },
  header: {
    fontSize: '24px',
  },
  runTypeLabel: {
    padding: '2% 0',
  },
  runNameLabel: {
    paddingTop: '3%',
  },
  button: {
    width: '140px',
    fontSize: '12px',
  },
  buttons: {
    paddingTop: 20,
    paddingRight: 30,
    paddingBottom: 30,
  },
  tooltip: {
    marginRight: 2,
    maxWidth: 220,
    cursor: 'default',
  },
};

function BaseRunScheduleModal({
  open,
  handleSave,
  handleClose,
  runSchedule,
  flags,
  selectedMarket,
  displayModalRadioSelector,
  editing,
  wranglers,
  zones,
  getVehiclesAndLocations,
  showErrorSnackbar,
  tz,
  equityDeployMarkets,
}) {
  const [currentRunSchedule, setCurrentRunSchedule] = useState(runSchedule);
  const dynamicRunFlagEnabled = !isEmpty(flags) && flags[0] && flags[0].isActive;
  const isDynamic = (dynamicRunFlagEnabled && get(currentRunSchedule, 'isDynamic', true))
    || (!dynamicRunFlagEnabled && get(currentRunSchedule, 'isDynamic', false));
  const [scheduleIsDynamic, setScheduleIsDynamic] = useState(isDynamic);

  const runScheduleDate = currentRunSchedule?.scheduledTime
    ? dayjs(currentRunSchedule.scheduledTime).tz(tz).format('YYYY-MM-DD')
    : '';
  const [selectedDate, setSelectedDate] = useState(runScheduleDate);

  let runScheduleTime;
  if (currentRunSchedule?.scheduledTime) {
    runScheduleTime = dayjs(currentRunSchedule.scheduledTime).tz(tz).format('HH:mm');
  } else runScheduleTime = '';
  const [startTime, setStartTime] = useState(runScheduleTime);

  const repeatOptions = [
    { label: 'Weekly', value: true },
    { label: 'Does not repeat', value: false },
  ];
  const sortedRepeatOptions = sortBy(repeatOptions, (type) => type.label.toLowerCase());

  const displayDynamicTab = dynamicRunFlagEnabled
    && scheduleIsDynamic !== false
    && !editing;
  const routeTypes = ['Static', 'Dynamic'];
  const sortedRouteTypes = sortBy(routeTypes, (type) => type);
  const intialRouteType = scheduleIsDynamic
    || displayDynamicTab ? first(sortedRouteTypes) : last(sortedRouteTypes);
  const [routeType, setRouteType] = useState(intialRouteType);
  const displayStatic = (editing && !scheduleIsDynamic)
    || !dynamicRunFlagEnabled
    || (routeType && routeType === 'Static');

  const assigneesExist = currentRunSchedule?.wranglers?.length >= 1;
  const [showAssignees, setShowAssignees] = useState(assigneesExist);
  const [assignees] = useState(undefined);
  const sortedWranglers = sortBy(wranglers, (wrangler) => wrangler.firstName.toLowerCase());
  const assigneeOptions = sortedWranglers.map((wrangler) => ({
    value: wrangler._id,
    label: `${wrangler.firstName} ${wrangler.lastName}`,
    isDisabled: false,
  }));

  const statusOptions = [
    { label: RunScheduleStatusNames.ACTIVE, value: RunScheduleStatusNames.ACTIVE },
    { label: RunScheduleStatusNames.INACTIVE, value: RunScheduleStatusNames.INACTIVE },
  ];
  const sortedStatusOptions = sortBy(statusOptions, (option) => option.label.toLowerCase());

  const sortedZones = sortBy(zones, (zone) => zone.name.toLowerCase());
  const zoneOptions = sortedZones.map((zone) => ({
    value: zone._id,
    label: zone.name,
    locations: zone.locations,
    isDisabled: false,
  }));
  const notEmptyZones = zoneOptions.filter((zone) => zone.locations.length);

  const allVehiclesToggleOptions = [
    { label: 'Yes', value: false },
    { label: 'No', value: true },
  ];

  const runScheduleTypes = [...sortedRunTypes];
  if (equityDeployMarkets && equityDeployMarkets.split(', ').includes(selectedMarket.name)) {
    runScheduleTypes.push({
      label: 'Equity Deployment', value: DataDriveRunTypes.EQUITY_DEPLOY,
    });
  }
  const runScheduleTypeOptions = sortBy(runScheduleTypes, (type) => type.label.toLowerCase());

  const [openPendingRunModal, setOpenPendingRunModal] = useState(false);
  const [pendingRunAssignees, setPendingRunAssignees] = useState(
    currentRunSchedule?.vanAssignees || {},
  );

  const onSave = (updatedScheduledRun = {}) => {
    if (!isEmpty(updatedScheduledRun)) handleSave(runSchedule, updatedScheduledRun);
    else handleSave(runSchedule, currentRunSchedule);
  };

  const onSaveRun = () => {
    let dateTime;
    const januaryTzString = DateTime.fromObject({
      year: 2000,
      month: 1,
    }).setZone(tz).offsetNameShort;
    const tzString = DateTime.local().setZone(tz).offsetNameShort;
    if (currentRunSchedule?.repeat) {
      if (startTime === '') {
        showErrorSnackbar('Please select a start time.');
        return;
      } else {
        dateTime = new Date(`2000-01-01 ${startTime} ${januaryTzString}`);
      }
    } else if (selectedDate !== '' && startTime !== '') {
      dateTime = new Date(`${selectedDate} ${startTime} ${tzString}`);
    } else if (selectedDate !== '') {
      dateTime = new Date(`${selectedDate} 12:00 ${tzString}`);
    }
    if (scheduleIsDynamic) {
      currentRunSchedule.routeIds = [];
      currentRunSchedule.wranglerIds = [];
      if (currentRunSchedule?.type === DataDriveRunTypes.REBALANCE) {
        delete currentRunSchedule.deployQuantity;
      }
    } else {
      currentRunSchedule.includeAllVehicles = false;
      currentRunSchedule.vanCount = 1;
      delete currentRunSchedule.vanCapacity;
      delete currentRunSchedule.type;
    }
    currentRunSchedule.scheduledTime = dateTime;
    currentRunSchedule.isDynamic = dynamicRunFlagEnabled ? scheduleIsDynamic : false;
    setCurrentRunSchedule(currentRunSchedule);
    if (currentRunSchedule.isDynamic && currentRunSchedule?.vanCount) {
      setOpenPendingRunModal(!openPendingRunModal);
    } else {
      onSave();
    }
  };

  const handleChange = (event, value) => {
    const fieldName = event.target.name;
    const newState = {
      ...currentRunSchedule,
      [fieldName]: fieldName && fieldName !== 'days'
        ? event.target.value
        : value.sort(),
    };
    setCurrentRunSchedule(newState);
  };

  const handleIncludeAllVehiclesChange = (event) => {
    const newState = {
      ...currentRunSchedule,
      includeAllVehicles: event.target.value === 'true',
    };
    setCurrentRunSchedule(newState);
  };

  const handleCountChange = (event) => {
    const count = parseFloat(event.target.value, 10);
    const newState = {
      ...currentRunSchedule,
      [event.target.name]: count,
    };
    setCurrentRunSchedule(newState);
  };

  const handleWranglerChange = (value) => {
    const newState = {
      ...currentRunSchedule,
      wranglerIds: value,
    };
    setCurrentRunSchedule(newState);
  };

  const handleRouteChange = (value = []) => {
    const newState = {
      ...currentRunSchedule,
      routeIds: value,
    };
    setCurrentRunSchedule(newState);
  };

  const handleZonesChange = (value = []) => {
    const newState = {
      ...currentRunSchedule,
      prioritizedZoneIds: value,
    };
    setCurrentRunSchedule(newState);
  };

  const handleClosePendingRunModal = () => {
    setOpenPendingRunModal(!openPendingRunModal);
  };

  if (openPendingRunModal) {
    return (
      <PendingRunModal
        openPendingRunModal={openPendingRunModal}
        currentRun={currentRunSchedule}
        setCurrentRun={setCurrentRunSchedule}
        handleClose={handleClosePendingRunModal}
        handleSaveRun={onSave}
        assigneeOptions={assigneeOptions}
        pendingRunAssignees={pendingRunAssignees}
        setPendingRunAssignees={setPendingRunAssignees}
        styles={styles}
      />
    );
  }

  return (
    <Dialog maxWidth={isOnMobile() ? 'sm' : 'lg'} open={open} onClose={handleClose}>
      <DialogTitle style={styles.flexRow}>
        <Typography style={styles.header}>{editing ? 'Edit a schedule' : 'Create a schedule'}</Typography>
      </DialogTitle>
      <DialogContent style={styles.contentStyle}>
        {dynamicRunFlagEnabled && displayModalRadioSelector && (
          <Grid>
            <Grid item container xs={12}>
              <RadioSelect
                value={routeType}
                valueArray={routeTypes}
                onClick={(e) => {
                  const dynamic = e.target.value === 'Dynamic';
                  setRouteType(e.target.value);
                  setScheduleIsDynamic(dynamic);
                  setCurrentRunSchedule({
                    ...currentRunSchedule,
                    isDynamic: dynamic,
                  });
                }}
              />
            </Grid>
          </Grid>
        )}
        <EditRunSchedule
          currentRunSchedule={currentRunSchedule}
          handleChange={handleChange}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          startTime={startTime}
          setStartTime={setStartTime}
          handleCountChange={handleCountChange}
          sortedRepeatOptions={sortedRepeatOptions}
          allVehiclesToggleOptions={allVehiclesToggleOptions}
          handleIncludeAllVehiclesChange={handleIncludeAllVehiclesChange}
          displayStatic={displayStatic}
          showAssignees={showAssignees}
          setShowAssignees={setShowAssignees}
          assignees={assignees}
          assigneeOptions={assigneeOptions}
          handleWranglerChange={handleWranglerChange}
          zoneOptions={notEmptyZones}
          handleRouteChange={handleRouteChange}
          editing={editing}
          sortedStatusOptions={sortedStatusOptions}
          selectedMarket={selectedMarket}
          getVehiclesAndLocations={getVehiclesAndLocations}
          tz={tz}
          runScheduleTypeOptions={runScheduleTypeOptions}
          handleZonesChange={handleZonesChange}
          styles={styles}
        />
      </DialogContent>
      <DialogActions style={styles.buttons}>
        <Button
          onClick={handleClose}
          color="primary"
          variant="outlined"
          style={styles.button}
        >
          Cancel
        </Button>
        <Button
          onClick={onSaveRun}
          color="primary"
          variant="contained"
          style={styles.button}
        >
          {displayStatic ? 'Save and Close' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

BaseRunScheduleModal.defaultProps = {};
BaseRunScheduleModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleSave: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  runSchedule: PropTypes.shape({
    _id: PropTypes.string,
  }).isRequired,
  flags: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
    }).isRequired,
  ).isRequired,
  selectedMarket: PropTypes.shape({
    _id: PropTypes.string,
  }).isRequired,
  displayModalRadioSelector: PropTypes.bool.isRequired,
  editing: PropTypes.bool.isRequired,
  wranglers: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
    }).isRequired,
  ).isRequired,
  zones: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
    }).isRequired,
  ).isRequired,
  showErrorSnackbar: PropTypes.func.isRequired,
  getVehiclesAndLocations: PropTypes.func.isRequired,
};

const RunScheduleModal = withRouter(BaseRunScheduleModal);
export { BaseRunScheduleModal, RunScheduleModal };
